import React, { useMemo, useState } from 'react';
import { DepartmentBadge, StatusBadge } from '../../../../shared/components/common/Badges';
import { formatPercentage } from '../../../../utils/numberUtils';
import TeamWorkloadShareHelpModal from './TeamWorkloadShareHelpModal';

const ASSIGNMENT_CV_THRESHOLD = 0.5;
const PACE_CAPTIONS = {
  magnet: 'Absorbing new tickets',
  holding: 'Holding the queue',
};

function coverageFrom(tech) {
  const coverage = tech.coverage || {};
  return {
    primary: coverage.primary || 0,
    secondary: coverage.secondary || 0,
    units: coverage.units || 0,
  };
}

function normalizeTechnician(tech) {
  const sla = tech.sla_compliance || {
    green: tech.sla_green || 0,
    amber: tech.sla_amber || 0,
    red: tech.sla_red || 0,
    total_closed: tech.sla_total_closed || 0,
    compliance_percent: tech.sla_compliance_percent || 0,
  };
  const share = tech.share || {};
  const pace = tech.pace || {};
  return {
    id: tech.id,
    name: tech.name || 'N/A',
    status: tech.status || 'in_share',
    active: tech.active_complaints ?? tech.active_assignments ?? 0,
    coverage: coverageFrom(tech),
    departments: Array.isArray(tech.departments) ? tech.departments : [],
    share: {
      expected_percent: share.expected_percent,
      open_percent: share.open_percent,
      open_ratio: share.open_ratio,
      received_ratio: share.received_ratio,
    },
    pace: {
      received_30d: pace.received_30d || 0,
      closed_30d: pace.closed_30d || 0,
      avg_close_days: pace.avg_close_days,
    },
    paceFlag: tech.pace_flag || null,
    sla: {
      green: sla.green || 0,
      amber: sla.amber || 0,
      red: sla.red || 0,
      total_closed: sla.total_closed || 0,
      compliance_percent: sla.compliance_percent || 0,
    },
  };
}

function coefficientOfVariation(values) {
  const sample = values.filter((value) => value != null);
  if (sample.length < 2) return 0;
  const mean = sample.reduce((sum, value) => sum + value, 0) / sample.length;
  if (mean <= 0) return 0;
  const variance = sample.reduce((sum, value) => sum + (value - mean) ** 2, 0) / sample.length;
  return Math.sqrt(variance) / mean;
}

function summaryFromRows(rows) {
  const units = rows.map((row) => row.coverage.units);
  const covered = units.filter((value) => value > 0);
  const uncovered = rows.filter((row) => row.coverage.units <= 0).length;
  const magnets = rows.filter((row) => row.paceFlag === 'magnet').length;
  const holding = rows.filter((row) => row.paceFlag === 'holding').length;
  let assignment_status = 'even';
  if ((uncovered === rows.length && rows.length > 0) || ((coefficientOfVariation(covered) >= ASSIGNMENT_CV_THRESHOLD || uncovered > 0) && covered.length > 0)) {
    assignment_status = 'uneven';
  }
  return {
    staffing_status: 'ok',
    assignment_status,
    routing_status: magnets || holding ? 'biased' : 'ok',
    created_30d: 0,
    closed_30d: rows.reduce((sum, row) => sum + (row.pace.closed_30d || 0), 0),
  };
}

function insightNotes(summary) {
  const notes = [{
    key: 'model',
    kind: 'legend',
    text: (
      <>
        Coverage is assigned devices: <strong>primary</strong> and <strong>secondary</strong>
        {' '}(secondary counts as ½ for share). Status is the queue versus those devices:
        {' '}<strong>heavy</strong>, <strong>fair</strong>, or <strong>light</strong>.
        {' '}On-time is last 30 days: complaints closed by location TAT and maintenance completed by due date.
        {' '}Open <strong>Share (i)</strong> for the live calculation on this location.
      </>
    ),
  }];
  const warnings = [];
  if (summary?.staffing_status === 'understaffed') {
    warnings.push(`Staffing: more tickets arriving than closing (${summary.created_30d || 0} new vs ${summary.closed_30d || 0} closed). That is a headcount gap, not a per-person cap.`);
  }
  if (summary?.assignment_status === 'uneven') {
    warnings.push('Device split: primary/secondary device counts are uneven. Ticket volume alone will look like a hero vs a slacker.');
  }
  if (summary?.routing_status === 'biased') {
    warnings.push('Auto-assign: the next ticket goes to whoever has fewer open. Fast closers get new work; slow closers hold the queue.');
  }
  if (warnings.length) {
    notes.push({ key: 'flags', kind: 'insight', warnings });
  }
  return notes;
}

function sortTechnicians(technicians, column, direction) {
  const statusPriority = {
    above_share: 4,
    no_coverage: 3,
    in_share: 2,
    below_share: 1,
    available: 0,
  };
  return [...technicians].sort((a, b) => {
    if (column === 'name') {
      const cmp = a.name.localeCompare(b.name);
      return direction === 'asc' ? cmp : -cmp;
    }
    let aValue;
    let bValue;
    if (column === 'status') {
      aValue = statusPriority[a.status] || 0;
      bValue = statusPriority[b.status] || 0;
    } else if (column === 'departments') {
      aValue = a.departments.length;
      bValue = b.departments.length;
    } else if (column === 'coverage') {
      aValue = a.coverage.units;
      bValue = b.coverage.units;
    } else if (column === 'load') {
      aValue = a.active;
      bValue = b.active;
    } else if (column === 'share') {
      aValue = a.share.open_ratio == null ? -1 : a.share.open_ratio;
      bValue = b.share.open_ratio == null ? -1 : b.share.open_ratio;
    } else if (column === 'pace') {
      aValue = a.pace.closed_30d;
      bValue = b.pace.closed_30d;
    } else {
      aValue = a.sla.total_closed > 0 ? a.sla.compliance_percent : -1;
      bValue = b.sla.total_closed > 0 ? b.sla.compliance_percent : -1;
    }
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });
}

function SortMark({ active, direction }) {
  if (!active) {
    return <span className="team-workload-sort">⇅</span>;
  }
  return (
    <span className="team-workload-sort team-workload-sort--active">
      {direction === 'desc' ? '↓' : '↑'}
    </span>
  );
}

function formatShareRatio(ratio) {
  if (ratio == null) return '—';
  const value = Number(ratio).toFixed(1).replace(/\.0$/, '');
  return `${value}×`;
}

function CoverageCell({ coverage }) {
  const primaryLabel = coverage.primary === 1 ? 'primary device' : 'primary devices';
  const secondaryLabel = coverage.secondary === 1 ? 'secondary device' : 'secondary devices';
  return (
    <div className="team-workload-coverage">
      <strong>{coverage.primary} {primaryLabel}</strong>
      <span>{coverage.secondary} {secondaryLabel}</span>
    </div>
  );
}

const MAX_VISIBLE_DEPARTMENTS = 3;

function DepartmentsCell({ departments }) {
  if (!departments.length) {
    return <span className="team-workload-depts__empty">None assigned</span>;
  }
  const extra = departments.length - MAX_VISIBLE_DEPARTMENTS;
  const visible = extra > 0 ? departments.slice(0, MAX_VISIBLE_DEPARTMENTS) : departments;
  const title = departments.map((dept) => dept.name).join(', ');
  return (
    <div className="team-workload-depts" title={title}>
      {visible.map((dept) => (
        <DepartmentBadge key={dept.id || dept.name} name={dept.name} size="small" />
      ))}
      {extra > 0 ? <span className="team-workload-depts__more">+{extra}</span> : null}
    </div>
  );
}

function ShareCell({ share }) {
  const ratio = share.open_ratio;
  const tone = ratio == null ? '' : ratio >= 1.35 ? 'high' : ratio <= 0.65 ? 'low' : 'ok';
  const fill = ratio == null ? 0 : Math.min(Math.max(ratio / 2, 0), 1) * 100;
  const expected = share.expected_percent == null ? '—' : formatPercentage(share.expected_percent, { decimals: 0 });
  return (
    <div className={`team-workload-share${tone ? ` is-${tone}` : ''}`}>
      <div className="team-workload-share__ratio">{formatShareRatio(ratio)}</div>
      <div
        className="team-workload-share__meter"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={2}
        aria-valuenow={ratio == null ? 0 : ratio}
        aria-label={`Open share ratio ${formatShareRatio(ratio)} versus expected ${expected}`}
      >
        <span style={{ width: `${fill}%` }} />
      </div>
      <div className="team-workload-share__meta">vs {expected} expected</div>
    </div>
  );
}

function PaceCell({ pace, paceFlag }) {
  const caption = PACE_CAPTIONS[paceFlag];
  const days = pace.avg_close_days == null ? null : Number(pace.avg_close_days).toFixed(1);
  return (
    <div className={`team-workload-pace${paceFlag ? ` is-${paceFlag}` : ''}`}>
      <div className="team-workload-pace__count">
        {pace.closed_30d} closed{days ? ` · ${days}d` : ''}
      </div>
      <div className="team-workload-pace__meta">
        {pace.received_30d} received
        {caption ? ` · ${caption}` : ''}
      </div>
    </div>
  );
}

function OnTimeCell({ sla }) {
  const total = sla.total_closed || 0;
  if (total <= 0) {
    return <span className="team-workload-sla__empty">No data</span>;
  }

  const tone = sla.compliance_percent >= 80 ? 'good' : sla.compliance_percent >= 60 ? 'watch' : 'poor';
  const title = `On time ${sla.green} · 1–7 days late ${sla.amber} · over 7 days ${sla.red}`;

  return (
    <div className={`team-workload-sla team-workload-sla--${tone}`} title={title}>
      <div className="team-workload-sla__pct">{formatPercentage(sla.compliance_percent)}</div>
      <div className="team-workload-sla__bar" aria-hidden="true">
        <span className="is-green" style={{ flex: `${sla.green} 0 0` }} />
        <span className="is-amber" style={{ flex: `${sla.amber} 0 0` }} />
        <span className="is-red" style={{ flex: `${sla.red} 0 0` }} />
      </div>
      <div className="team-workload-sla__meta">
        {total} closed · {sla.green} on time
      </div>
    </div>
  );
}

export default function TeamWorkloadTable({ technicians = [], summary = null }) {
  const [sortColumn, setSortColumn] = useState('share');
  const [sortDirection, setSortDirection] = useState('desc');
  const [shareHelpOpen, setShareHelpOpen] = useState(false);

  const normalized = useMemo(
    () => technicians.map(normalizeTechnician),
    [technicians]
  );

  const rows = useMemo(
    () => sortTechnicians(normalized, sortColumn, sortDirection),
    [normalized, sortColumn, sortDirection]
  );

  const notes = useMemo(
    () => insightNotes(summary || summaryFromRows(normalized)),
    [summary, normalized]
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <>
      <div className="team-workload-notes">
        {notes.map((note) => (
          <div
            className={`list-default-window-note${note.kind === 'insight' ? ' list-default-window-note--insight' : ''}`}
            role={note.kind === 'insight' ? 'status' : 'note'}
            key={note.key}
          >
            <span className="list-default-window-note__icon" aria-hidden="true">
              {note.kind === 'insight' ? '⚠️' : 'ℹ️'}
            </span>
            {note.warnings ? (
              <ul className="team-workload-notes__warnings">
                {note.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : (
              <span>{note.text}</span>
            )}
          </div>
        ))}
      </div>
      <div className="team-workload-table-wrap">
        <table className="team-workload-table">
          <thead>
            <tr>
              <th
                className={`sortable${sortColumn === 'name' ? ' sort-active' : ''}`}
                onClick={() => handleSort('name')}
              >
                Technician <SortMark active={sortColumn === 'name'} direction={sortDirection} />
              </th>
              <th
                className={`sortable${sortColumn === 'status' ? ' sort-active' : ''}`}
                onClick={() => handleSort('status')}
              >
                Status <SortMark active={sortColumn === 'status'} direction={sortDirection} />
              </th>
              <th
                className={`sortable${sortColumn === 'departments' ? ' sort-active' : ''}`}
                onClick={() => handleSort('departments')}
              >
                Departments <SortMark active={sortColumn === 'departments'} direction={sortDirection} />
              </th>
              <th
                className={`sortable${sortColumn === 'coverage' ? ' sort-active' : ''}`}
                onClick={() => handleSort('coverage')}
              >
                Coverage <SortMark active={sortColumn === 'coverage'} direction={sortDirection} />
              </th>
              <th
                className={`sortable num${sortColumn === 'load' ? ' sort-active' : ''}`}
                onClick={() => handleSort('load')}
              >
                Load <SortMark active={sortColumn === 'load'} direction={sortDirection} />
              </th>
              <th
                className={`sortable${sortColumn === 'share' ? ' sort-active' : ''}`}
                onClick={() => handleSort('share')}
              >
                <span className="team-workload-th-label">
                  Share <SortMark active={sortColumn === 'share'} direction={sortDirection} />
                  <button
                    type="button"
                    className="team-workload-info-btn"
                    title="How share is calculated for this location"
                    aria-label="How share is calculated for this location"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShareHelpOpen(true);
                    }}
                  >
                    ℹ️
                  </button>
                </span>
              </th>
              <th
                className={`sortable${sortColumn === 'pace' ? ' sort-active' : ''}`}
                onClick={() => handleSort('pace')}
              >
                Pace (30d) <SortMark active={sortColumn === 'pace'} direction={sortDirection} />
              </th>
              <th
                className={`sortable${sortColumn === 'sla' ? ' sort-active' : ''}`}
                onClick={() => handleSort('sla')}
              >
                On-time (30d) <SortMark active={sortColumn === 'sla'} direction={sortDirection} />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((tech) => {
              const aboveShare = tech.status === 'above_share';
              return (
                <tr key={tech.id || tech.name} className={aboveShare ? 'is-above-share' : undefined}>
                  <td className="team-workload-name">{tech.name}</td>
                  <td>
                    <StatusBadge status={tech.status} size="small" variant="pill" />
                  </td>
                  <td>
                    <DepartmentsCell departments={tech.departments} />
                  </td>
                  <td>
                    <CoverageCell coverage={tech.coverage} />
                  </td>
                  <td className="num team-workload-load-count">{tech.active}</td>
                  <td>
                    <ShareCell share={tech.share} />
                  </td>
                  <td>
                    <PaceCell pace={tech.pace} paceFlag={tech.paceFlag} />
                  </td>
                  <td>
                    <OnTimeCell sla={tech.sla} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TeamWorkloadShareHelpModal
        isOpen={shareHelpOpen}
        onClose={() => setShareHelpOpen(false)}
        technicians={rows}
      />
    </>
  );
}
